import { lazy, Suspense, useLayoutEffect } from "react";
import { Toaster } from 'sonner';
import { Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import { BookingProvider, useBooking } from "./contexts/BookingContext";

// All non-index pages lazy loaded to minimize main-thread work
const JornadaPage = lazy(() => import("./pages/JornadaPage"));
const SobrePage = lazy(() => import("./pages/SobrePage"));
const ContatoPage = lazy(() => import("./pages/ContatoPage"));
const GlossarioPage = lazy(() => import("./pages/GlossarioPage"));
const MapaDoSitePage = lazy(() => import("./pages/MapaDoSitePage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const PoliticaPrivacidadePage = lazy(() => import("./pages/PoliticaPrivacidadePage"));
const TermosUsoPage = lazy(() => import("./pages/TermosUsoPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Procedure pages — lazy loaded
const AbdominoplastiaPage = lazy(() => import("./pages/procedimentos/AbdominoplastiaPage"));
const BlefaroplastiaPage = lazy(() => import("./pages/procedimentos/BlefaroplastiaPage"));
const CirurgiasCombinavelPage = lazy(() => import("./pages/procedimentos/CirurgiasCombinavelPage"));
const CorrecaoCicatrizesPage = lazy(() => import("./pages/procedimentos/CorrecaoCicatrizesPage"));
const EnxertiaGorduraMamaPage = lazy(() => import("./pages/procedimentos/EnxertiaGorduraMamaPage"));
const LipoaspiracaoEnxertiaGluteaPage = lazy(() => import("./pages/procedimentos/LipoaspiracaoEnxertiaGluteaPage"));
const LipoaspiracaoPage = lazy(() => import("./pages/procedimentos/LipoaspiracaoPage"));
const LipoenxertiaFacePage = lazy(() => import("./pages/procedimentos/LipoenxertiaFacePage"));
const MamoplastiaAumentoPage = lazy(() => import("./pages/procedimentos/MamoplastiaAumentoPage"));
const MamoplastiaRedutoraPage = lazy(() => import("./pages/procedimentos/MamoplastiaRedutoraPage"));
const MastopexiaComProtesePage = lazy(() => import("./pages/procedimentos/MastopexiaComProtesePage"));
const MastopexiaSemProtesePage = lazy(() => import("./pages/procedimentos/MastopexiaSemProtesePage"));
const MiniAbdominoplastiaPage = lazy(() => import("./pages/procedimentos/MiniAbdominoplastiaPage"));
const MommyMakeoverPage = lazy(() => import("./pages/procedimentos/MommyMakeoverPage"));
const OtoplastiaPage = lazy(() => import("./pages/procedimentos/OtoplastiaPage"));
const RetiradaTumoresPelePage = lazy(() => import("./pages/procedimentos/RetiradaTumoresPelePage"));
const TrocaProteseMamariaPage = lazy(() => import("./pages/procedimentos/TrocaProteseMamariaPage"));
const LiftingCorporalPage = lazy(() => import("./pages/procedimentos/LiftingCorporalPage"));
const PreenchimentoFacialPage = lazy(() => import("./pages/procedimentos/PreenchimentoFacialPage"));
const ToxinaBotulinicaPage = lazy(() => import("./pages/procedimentos/ToxinaBotulinicaPage"));

// Admin — lazy loaded (includes its own auth context)
const AdminRoutes = lazy(() => import("./components/admin/AdminRoutes"));

// UI overlays — lazy
const CookieConsent = lazy(() =>
  import("@/components/ui/cookie-consent").then((m) => ({ default: m.CookieConsent }))
);
const WhatsAppPopup = lazy(() => import("@/components/sections/WhatsAppPopup"));
const SuccessPopup = lazy(() => import("@/components/ui/success-popup"));

// Page loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-dark-base">
    <div className="w-8 h-8 border-2 border-light-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const AnimatedRoutes = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    const id = setTimeout(() => { document.documentElement.style.scrollBehavior = ''; }, 50);
    return () => clearTimeout(id);
  }, [pathname]);

  return (
    <div key={pathname} className="page-transition">
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/jornada" element={<JornadaPage />} />
          <Route path="/sobre" element={<SobrePage />} />
          <Route path="/contato" element={<ContatoPage />} />
          <Route path="/glossario" element={<GlossarioPage />} />
          <Route path="/mapa-do-site" element={<MapaDoSitePage />} />
          <Route path="/procedimentos/abdominoplastia" element={<AbdominoplastiaPage />} />
          <Route path="/procedimentos/blefaroplastia" element={<BlefaroplastiaPage />} />
          <Route path="/procedimentos/cirurgias-combinadas" element={<CirurgiasCombinavelPage />} />
          <Route path="/procedimentos/correcao-de-cicatrizes" element={<CorrecaoCicatrizesPage />} />
          <Route path="/procedimentos/enxertia-de-gordura-na-mama" element={<EnxertiaGorduraMamaPage />} />
          <Route path="/procedimentos/lipoaspiracao-com-enxertia-glutea" element={<LipoaspiracaoEnxertiaGluteaPage />} />
          <Route path="/procedimentos/lipoaspiracao" element={<LipoaspiracaoPage />} />
          <Route path="/procedimentos/lipoenxertia-na-face" element={<LipoenxertiaFacePage />} />
          <Route path="/procedimentos/mamoplastia-de-aumento" element={<MamoplastiaAumentoPage />} />
          <Route path="/procedimentos/mamoplastia-redutora" element={<MamoplastiaRedutoraPage />} />
          <Route path="/procedimentos/mastopexia-com-protese" element={<MastopexiaComProtesePage />} />
          <Route path="/procedimentos/mastopexia-sem-protese" element={<MastopexiaSemProtesePage />} />
          <Route path="/procedimentos/mini-abdominoplastia" element={<MiniAbdominoplastiaPage />} />
          <Route path="/procedimentos/mommy-makeover" element={<MommyMakeoverPage />} />
          <Route path="/procedimentos/otoplastia" element={<OtoplastiaPage />} />
          <Route path="/procedimentos/retirada-de-tumores-de-pele" element={<RetiradaTumoresPelePage />} />
          <Route path="/procedimentos/troca-de-protese-mamaria" element={<TrocaProteseMamariaPage />} />
          <Route path="/procedimentos/lifting-corporal-pos-perda-de-peso" element={<LiftingCorporalPage />} />
          <Route path="/procedimentos/preenchimento-facial" element={<PreenchimentoFacialPage />} />
          <Route path="/procedimentos/toxina-botulinica" element={<ToxinaBotulinicaPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
          <Route path="/politica-de-privacidade" element={<PoliticaPrivacidadePage />} />
          <Route path="/termos-de-uso" element={<TermosUsoPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
};

const GlobalPopups = () => {
  const {
    isWhatsAppOpen, setIsWhatsAppOpen,
    isSuccessPopupOpen,
    countdown,
    whatsAppFormData, setWhatsAppFormData,
    hasStartedWhatsAppForm,
    handleWhatsAppOpen,
    handleWhatsAppFormSubmit,
    handleWhatsAppPhoneChange,
    handleCountryChange,
    handleWhatsAppFormFieldChange,
    handleSuccessRedirect,
    handleSuccessClose,
  } = useBooking();

  return (
    <>
      <Suspense fallback={null}>
        <WhatsAppPopup
          isWhatsAppOpen={isWhatsAppOpen}
          setIsWhatsAppOpen={setIsWhatsAppOpen}
          whatsAppFormData={whatsAppFormData}
          setWhatsAppFormData={setWhatsAppFormData}
          handleWhatsAppFormSubmit={handleWhatsAppFormSubmit}
          handleWhatsAppPhoneChange={handleWhatsAppPhoneChange}
          handleCountryChange={handleCountryChange}
          handleWhatsAppFormFieldChange={handleWhatsAppFormFieldChange}
          handleWhatsAppOpen={handleWhatsAppOpen}
          hasStartedWhatsAppForm={hasStartedWhatsAppForm}
        />
      </Suspense>
      {isSuccessPopupOpen && (
        <Suspense fallback={null}>
          <SuccessPopup
            isOpen={isSuccessPopupOpen}
            onClose={handleSuccessClose}
            onRedirect={handleSuccessRedirect}
            countdown={countdown}
          />
        </Suspense>
      )}
    </>
  );
};

const App = () => (
  <BookingProvider>
    <Toaster position="top-right" richColors />
    <Suspense fallback={null}>
      <CookieConsent />
    </Suspense>
    <AnimatedRoutes />
    <GlobalPopups />
  </BookingProvider>
);

export default App;
